import React, { useState, useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Popover, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/solid';

// Improved Navbar component with elegant dropdown menus
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  // Add scroll event listener to detect when user scrolls
  useEffect(() => {
    const handleScroll = () => {
      // Adjust threshold as needed
      const scrollThreshold = 80;
      setIsScrolled(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Navigation item definitions with their dropdown menus
  const navItems = [
    { name: 'HOME', path: '/', dropdown: null },
    { 
      name: 'ABOUT US', 
      path: '/about', 
      dropdown: [
        { name: 'About Us', path: '/about' },
        { name: 'Contact Us', path: '/contact-us' },
      ]
    },
    { 
      name: 'PARENT COORDINATOR CORNER', 
      path: '/parent-coordinator-corner', 
      dropdown: null 
    },
    { 
      name: 'PARENT RESOURCES', 
      path: '/parent-resources', 
      dropdown: [
        { name: 'Afterschool NIA', path: '/parent-resources/afterschool-nia' },
        { name: 'Assessments', path: 'https://www.schools.nyc.gov/learning/testing', externalLink: true },
        { name: 'Bus Information', path: '/parent-resources/bus-info' },
        { name: 'Cellphone Policy', path: '/parent-resources/cellphone-policy' },
        { name: 'Discipline Code', path: 'https://4.files.edl.io/8fff/09/06/19/140232-484f5fed-b944-441e-8a8a-51e391c32eea.pdf', externalLink: true },
        { name: 'Dismissal Locations', path: '/parent-resources/dismissal-locations' },
        { name: 'ESL', path: '/parent-resources/esl' },
        { name: 'IEP Individual Education Plan', path: '/parent-resources/iep' },
        { name: 'NYC Student Account', path: 'https://www.schoolsaccount.nyc/', externalLink: true },
        { name: 'Operoo', path: 'https://app.operoo.com/users/sign_in?_gl=1*a63h65*_gcl_au*MTgxNzM0OTgyNS4xNzQ3ODM3OTM3*_ga*MTkzODYyMjc1MS4xNzQ3ODM3OTQw*_ga_CYM1PT3B2D*czE3NDc4Mzc5MzYkbzEkZzAkdDE3NDc4Mzc5MzYkajYwJGwwJGgwJGRGTkhKVUdGclBkQk50R1o2TjlCQi1Wcl9VTldhTlJnT3Nn', externalLink: true },
        { name: 'Parent Handbook', path: '/parent-resources/parent-handbook' },
        { name: 'Student Food Menu', path: 'https://www.schools.nyc.gov/school-life/food/menus', externalLink: true },
      ]
    },
    { 
      name: 'CLASSES', 
      path: '/classes', 
      dropdown: [
        { name: 'Pre-K', path: '/classes/pre-k' },
        { name: 'Kindergarten', path: '/classes/kindergarten' },
        { name: 'First Grade', path: '/classes/first-grade' },
        { name: 'Second Grade', path: '/classes/second-grade' },
        { name: 'Third Grade', path: '/classes/third-grade' },
        { name: 'Fourth Grade', path: '/classes/fourth-grade' },
        { name: 'Fifth Grade', path: '/classes/fifth-grade' },
        { name: 'Science', path: '/classes/science' },
        { name: 'Social Studies', path: '/classes/social-studies' },
        { name: 'Physical Education', path: '/classes/physical-education' },
        { name: 'Library/Tech', path: '/classes/library-tech' },
        { name: 'Music', path: '/classes/music' },
      ]
    },
    { 
      name: 'STUDENTS', 
      path: '/students', 
      dropdown: [
        { name: 'Google Classroom', path: 'https://classroom.google.com', externalLink: true },
        { name: 'Clever', path: 'https://clever.com/oauth/authorize?channel=clever&client_id=4c63c1cf623dce82caac&confirmed=true&redirect_uri=https%3A%2F%2Fclever.com%2Fin%2Fauth_callback&response_type=code&state=0a4184047944a05209717efb534f1b81623be50d50c4b124060c06c3ebe9fc23&district_id=54d263946cc968010000007a', externalLink: true },
        { name: 'Waggle', path: 'https://papi.hmhco.com/login/initialize?iss=hmh-prod-4383c407-584d-4b6f-ad21-164c9c4bdabb', externalLink: true },
      ]
    },
    { 
      name: 'LEARNING RESOURCES', 
      path: '/learning-resources', 
      dropdown: [
        { name: 'Clever', path: 'https://clever.com/oauth/authorize?channel=clever&client_id=4c63c1cf623dce82caac&confirmed=true&redirect_uri=https%3A%2F%2Fclever.com%2Fin%2Fauth_callback&response_type=code&state=0a4184047944a05209717efb534f1b81623be50d50c4b124060c06c3ebe9fc23&district_id=54d263946cc968010000007a', externalLink: true },
        { name: 'HMH/Waggle', path: 'https://papi.hmhco.com/login/initialize?iss=hmh-prod-4383c407-584d-4b6f-ad21-164c9c4bdabb', externalLink: true },
        { name: 'Zearn', path: 'https://www.zearn.org/', externalLink: true },
        { name: 'DataMate', path: 'https://apps.edvistas.com/evicore/', externalLink: true },
        { name: 'Google Classroom', path: 'https://classroom.google.com', externalLink: true },
        { name: 'Google Meet', path: 'https://meet.google.com', externalLink: true },
        { name: 'Raz Kids', path: 'https://www.kidsa-z.com/ng/', externalLink: true },
        { name: 'Joy School', path: 'https://play.joyschoolenglish.com/', externalLink: true },
        { name: 'Amplify', path: 'https://my.amplify.com/auth/realms/Amplify/protocol/openid-connect/auth?client_id=learning-home&redirect_uri=https%3A%2F%2Flearning.amplify.com%2Fhome%2F&state=6d2cbce9-9e65-4ef6-891c-3956c1700cb7&response_mode=fragment&response_type=code&scope=openid&nonce=b94595d1-6eb4-40a9-b48f-857353445e28', externalLink: true },
      ]
    },
    { name: 'ONLINE STORE', path: '/store', dropdown: null },
  ];

  // Dynamic style for navbar positioning based on scroll
  const navStyle = isScrolled 
    ? { top: 0, position: 'fixed', width: '100%' } 
    : { top: 0 };

  // Render dropdown menu for navigation items using Popover for elegance
  const renderNavItem = (item, index) => {
    if (!item.dropdown) {
      return (
        <Link 
          to={item.path} 
          className="text-white text-sm font-medium tracking-wide relative group px-3 py-2 flex items-center justify-center"
        >
          <span className="relative text-center">
            {item.name}
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-500 group-hover:w-full transition-all duration-300"></span>
          </span>
        </Link>
      );
    }

    return (
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button 
              className="text-white text-sm font-medium tracking-wide relative group focus:outline-none px-3 py-2 flex items-center"
            >
              <div className="flex items-center justify-center">
                <span className="relative text-center">
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-500 group-hover:w-full transition-all duration-300"></span>
                </span>
                <ChevronDownIcon
                  className={`ml-1 h-4 w-4 text-white transition-transform duration-200 ${
                    open ? 'transform rotate-180' : ''
                  }`}
                />
              </div>
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 w-64 mt-2 px-2 sm:px-0">
                <div className="rounded-md shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
                  <div className="relative bg-white py-2">
                    {item.dropdown.map((subItem, idx) => (
                      <div key={idx} className="border-b border-gray-50 last:border-0">
                        {subItem.externalLink ? (
                          <a
                            href={subItem.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            {subItem.name}
                          </a>
                        ) : (
                          <Link
                            to={subItem.path}
                            className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            {subItem.name}
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  return (
    <nav 
      style={navStyle}
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled 
        ? 'bg-[#2a4865] shadow-md' 
        : 'bg-gradient-to-b from-black/50 to-transparent backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center px-4 py-2 lg:px-6">
          {/* Logo and School Name */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center text-white no-underline">
              <div className="h-28 w-28 mr-3 flex-shrink-0">
                <img 
                  src="/images/navbar/logo.png" 
                  alt="PS52 Logo" 
                  className="h-full w-full object-contain" 
                />
              </div>
              <div className="flex flex-col">
                <div className="text-lg font-semibold leading-tight tracking-wide">P.S. 052</div>
                <div className="text-sm font-medium leading-tight tracking-normal">John C. Thompson</div>
              </div>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex xl:hidden">
            <Popover>
              {({ open, close: closeMobilePopover }) => (
                <>
                  <Popover.Button className="inline-flex items-center justify-center p-2 rounded-md text-white focus:outline-none">
                    <span className="sr-only">Open main menu</span>
                    <div className="relative w-6 h-5">
                      <span className={`absolute h-0.5 w-full bg-white transform transition-all duration-300 ${open ? 'rotate-45 translate-y-2' : 'translate-y-0'}`}></span>
                      <span className={`absolute h-0.5 mt-2 w-full bg-white transform transition-all duration-300 ${open ? 'opacity-0' : 'opacity-100'}`}></span>
                      <span className={`absolute h-0.5 mt-4 w-full bg-white transform transition-all duration-300 ${open ? '-rotate-45 -translate-y-2' : 'translate-y-0'}`}></span>
                    </div>
                  </Popover.Button>

                  <Transition
                    as={Fragment}
                    enter="duration-200 ease-out"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="duration-100 ease-in"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Popover.Panel
                      focus
                      className="absolute top-full left-0 right-0 p-2 bg-[#2a4865] shadow-lg overflow-y-auto max-h-[85vh]"
                    >
                      <div className="rounded-md shadow-xs py-1">
                        {navItems.map((item, index) => (
                          <div key={index} className="border-b border-[#3b5d82]/40 last:border-0">
                            {item.dropdown ? (
                              <Disclosure>
                                {({ open }) => (
                                  <>
                                    <Disclosure.Button className="flex justify-between w-full px-4 py-3 text-sm font-medium text-white">
                                      <span>{item.name}</span>
                                      <ChevronDownIcon
                                        className={`${
                                          open ? 'transform rotate-180' : ''
                                        } w-5 h-5 text-white`}
                                      />
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="px-4 pt-2 pb-4 text-sm">
                                      <div className="space-y-1 pl-2 border-l-2 border-[#3b5d82]">
                                        {item.dropdown.map((subItem, idx) => (
                                          <div key={idx} className="py-1">
                                            {subItem.externalLink ? (
                                              <a
                                                href={subItem.path}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block text-white opacity-80 hover:opacity-100"
                                                onClick={closeMobilePopover}
                                              >
                                                {subItem.name}
                                              </a>
                                            ) : (
                                              <Link
                                                to={subItem.path}
                                                className="block text-white opacity-80 hover:opacity-100"
                                                onClick={closeMobilePopover}
                                              >
                                                {subItem.name}
                                              </Link>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </Disclosure.Panel>
                                  </>
                                )}
                              </Disclosure>
                            ) : (
                              <Link
                                to={item.path}
                                className="block px-4 py-3 text-sm font-medium text-white"
                                onClick={closeMobilePopover}
                              >
                                {item.name}
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex xl:items-center xl:space-x-2 xl:space-x-4 pb-2">
            {navItems.map((item, index) => (
              <div key={index} className="flex items-center text-center">
                {renderNavItem(item, index)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

// Import missing Disclosure component
const Disclosure = ({ children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <>
      {children({ 
        open: isOpen, 
        close: () => setIsOpen(false),
        toggle: () => setIsOpen(!isOpen)
      })}
    </>
  );
};

// Add Disclosure.Button and Disclosure.Panel components
Disclosure.Button = ({ children, className, onClick }) => {
  return (
    <button 
      className={className} 
      onClick={(e) => {
        e.preventDefault();
        if (onClick) onClick(e);
      }}
    >
      {typeof children === 'function' 
        ? children({ open: false }) 
        : children}
    </button>
  );
};

Disclosure.Panel = ({ children, className }) => {
  return (
    <div className={className}>
      {children}
    </div>
  );
};

export default Navbar;